import { useEffect, useRef } from 'react';
import {
  FT_INNER,
  FT_MIDDLE,
  FT_OUTER,
  DEFAULT_ARROW_LENGTH_FT,
  METERS_PER_FOOT,
  MIN_DRAG_FT,
  computeBearingDeg,
  computeDistanceFeet,
} from './isdBlockGeometry';

const COLOR_OPPOSITE = '#22D3EE'; // cyan
const COLOR_OPPOSITE_HALO = '#000000';

/**
 * Click-drag placement for the F-case ISD opposite block.
 *
 * Mirrors `useIsdBlockDrawing`'s primary-block placement gesture but:
 *   - Uses a cyan preview to distinguish from the yellow primary block.
 *   - On mouseup, calls `onOppositeBlockPicked({ insertionPoint, azimuth, arrowLengthFeet })`.
 *   - Esc cancels via `onCancelOppositePointPick`.
 *
 * Active only while `oppositePointPickActive === true` and we're in the
 * intersection panel mode.
 */
export function useIsdOppositePointPick({
  map,
  pathsPanelMode,
  oppositePointPickActive,
  onOppositeBlockPicked,
  onCancelOppositePointPick,
}) {
  const onPickRef = useRef(onOppositeBlockPicked);
  onPickRef.current = onOppositeBlockPicked;
  const onCancelRef = useRef(onCancelOppositePointPick);
  onCancelRef.current = onCancelOppositePointPick;

  useEffect(() => {
    if (!map || !window.google?.maps?.event) return;
    if (pathsPanelMode !== 'intersection') return;
    if (!oppositePointPickActive) return;

    const g = window.google.maps;
    const mapDiv = map.getDiv();
    const previousCursor = mapDiv?.style.cursor ?? '';
    if (mapDiv) mapDiv.style.cursor = 'crosshair';

    let preview = null;
    let center = null;
    let mouseMoveListener = null;
    let mouseUpListener = null;
    let isCreating = false;

    const createPreview = (origin) => {
      const mainOpts = (radFt, fill) => ({
        map,
        center: origin,
        radius: radFt * METERS_PER_FOOT,
        strokeColor: COLOR_OPPOSITE,
        strokeWeight: 2,
        fillColor: COLOR_OPPOSITE,
        fillOpacity: fill,
        clickable: false,
        zIndex: 22,
      });
      const haloOpts = (radFt) => ({
        map,
        center: origin,
        radius: radFt * METERS_PER_FOOT,
        strokeColor: COLOR_OPPOSITE_HALO,
        strokeWeight: 4,
        fillOpacity: 0,
        clickable: false,
        zIndex: 20,
      });
      return {
        innerHalo: new g.Circle(haloOpts(FT_INNER)),
        middleHalo: new g.Circle(haloOpts(FT_MIDDLE)),
        outerHalo: new g.Circle(haloOpts(FT_OUTER)),
        arrowHalo: new g.Polyline({
          map,
          path: [origin, origin],
          strokeColor: COLOR_OPPOSITE_HALO,
          strokeWeight: 4,
          clickable: false,
          zIndex: 21,
        }),
        inner: new g.Circle(mainOpts(FT_INNER, 0.15)),
        middle: new g.Circle({ ...mainOpts(FT_MIDDLE, 0), fillOpacity: 0 }),
        outer: new g.Circle({ ...mainOpts(FT_OUTER, 0), fillOpacity: 0 }),
        arrow: new g.Polyline({
          map,
          path: [origin, origin],
          strokeColor: COLOR_OPPOSITE,
          strokeWeight: 2,
          clickable: false,
          zIndex: 23,
          icons: [{
            icon: {
              path: g.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 3.5,
              strokeColor: COLOR_OPPOSITE_HALO,
              strokeWeight: 1,
              fillColor: COLOR_OPPOSITE,
              fillOpacity: 1,
            },
            offset: '100%',
          }],
        }),
      };
    };

    const destroyPreview = () => {
      if (!preview) return;
      preview.inner.setMap(null);
      preview.middle.setMap(null);
      preview.outer.setMap(null);
      preview.arrow.setMap(null);
      preview.innerHalo?.setMap(null);
      preview.middleHalo?.setMap(null);
      preview.outerHalo?.setMap(null);
      preview.arrowHalo?.setMap(null);
      preview = null;
    };

    const mouseDownListener = g.event.addListener(map, 'mousedown', (event) => {
      if (!event.latLng) return;
      isCreating = true;
      center = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      preview = createPreview(center);
      map.setOptions({ draggable: false });

      mouseMoveListener = g.event.addListener(map, 'mousemove', (moveEvent) => {
        if (!moveEvent.latLng || !preview) return;
        const cursor = { lat: moveEvent.latLng.lat(), lng: moveEvent.latLng.lng() };
        preview.arrow.setPath([center, cursor]);
        preview.arrowHalo?.setPath([center, cursor]);
      });

      mouseUpListener = g.event.addListener(map, 'mouseup', (upEvent) => {
        if (!isCreating) return;
        isCreating = false;
        map.setOptions({ draggable: true });

        const cursor = upEvent.latLng
          ? { lat: upEvent.latLng.lat(), lng: upEvent.latLng.lng() }
          : center;
        const distFt = computeDistanceFeet(center, cursor);
        const azimuth = distFt >= MIN_DRAG_FT ? computeBearingDeg(center, cursor) : 0;
        const arrowLengthFeet = distFt >= MIN_DRAG_FT ? distFt : DEFAULT_ARROW_LENGTH_FT;

        destroyPreview();
        if (mouseMoveListener) g.event.removeListener(mouseMoveListener);
        if (mouseUpListener) g.event.removeListener(mouseUpListener);
        mouseMoveListener = null;
        mouseUpListener = null;

        onPickRef.current?.({
          insertionPoint: center,
          azimuth,
          arrowLengthFeet,
        });
      });
    });

    const onKey = (ev) => {
      if (ev.key === 'Escape') onCancelRef.current?.();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      if (mapDiv) mapDiv.style.cursor = previousCursor;
      if (mouseDownListener) g.event.removeListener(mouseDownListener);
      if (mouseMoveListener) g.event.removeListener(mouseMoveListener);
      if (mouseUpListener) g.event.removeListener(mouseUpListener);
      document.removeEventListener('keydown', onKey);
      map.setOptions({ draggable: true });
      destroyPreview();
    };
  }, [map, pathsPanelMode, oppositePointPickActive]);
}
